import { StatusSourceType, subscribeToLiveStatus } from "@/stores/live-status";
import { StationStatusState, StatusDataType } from "@soft-db/monitoring-api-client";
import { useCachedStations } from "../../../api/station-query";
import { useSyncDate } from "../../../use/sync-date";

export type LiveStatus = {
	time: Date;
	state: StationStatusState;
	value: number | null;
	battery: number | null;
	type: StatusDataType;
	charging: boolean;
};

export const useLiveStatus = (
	type: MaybeRefOrGetter<StatusSourceType>,
	id: MaybeRefOrGetter<string>
) => {
	const status = subscribeToLiveStatus(type, id);

	const cachedStations = useCachedStations();

	const dataType = computed(() => {
		if (status.value == null) {
			return StatusDataType.SLM;
		}

		const { station } = status.value;

		// Get the data type from the station if it's in cache, else get the one from the status
		// Allow status data type change to be seen instantly.
		return cachedStations.value.get(station)?.data.statusDataType ?? status.value.type;
	});

	const dataPoints = computed(() => {
		if (status.value == null) {
			return [];
		}

		switch (dataType.value) {
			case StatusDataType.SLM:
				return status.value.sound || [];

			case StatusDataType.ACC:
				return status.value.accelerationPeak || [];

			case StatusDataType.VEL:
				return status.value.velocityPeak || [];

			case StatusDataType.OPL:
				return status.value.overpressure || [];

			default:
				return [];
		}
	});

	const currentIndex = ref(0);

	const syncDate = useSyncDate();
	function updateCurrentIndex() {
		if (status.value == null || dataPoints.value.length === 0) {
			return;
		}

		const time = syncDate.now();
		const statusLength = dataPoints.value.length;
		const offset = time - new Date(status.value.time).getTime();
		const statusDuration = status.value.dt * statusLength;

		currentIndex.value = Math.min(
			Math.round((offset / statusDuration) * (statusLength - 1)),
			statusLength - 1
		);
	}

	useIntervalFn(updateCurrentIndex, 1_000);

	watch(
		status,
		() => {
			updateCurrentIndex();
		},
		// Using a sync flush, so we reset the currentIndex before doing the current data update
		{ flush: "sync" }
	);

	const currentDataPoint = computed(() => {
		return dataPoints.value.at(currentIndex.value) ?? null;
	});

	return computed<LiveStatus | null>(() => {
		if (status.value == null) {
			return null;
		}

		return {
			time: new Date(status.value.time),
			state: status.value.state,
			charging: status.value.isCharging,
			battery: status.value.powerLevel,
			type: dataType.value,
			value: currentDataPoint.value,
		};
	});
};
